/**
 * Shared primitives.
 *
 * Small on purpose — the portal is dense, data-heavy screens, not a design showcase.
 */

import { forwardRef, type ReactNode } from 'react';

import { useI18n } from '@/i18n';
import type { Tone } from '@/lib/labels';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-surface-alt text-muted',
  primary: 'bg-[var(--color-primary-soft)] text-primary',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
};

export function Badge({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode;
  tone?: Tone;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * Status dot + label.
 *
 * Deliberately never a bare colour: the shape and the word carry the meaning too.
 */
export function StatusDot({ tone, children }: { tone: Tone; children: ReactNode }) {
  const dot: Record<Tone, string> = {
    neutral: 'bg-subtle',
    primary: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
  };

  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <span className={`w-2 h-2 rounded-full shrink-0 ${dot[tone]}`} aria-hidden />
      {children}
    </span>
  );
}

/** Ref-forwarding so a caller can scroll a card into view — see the Alerts page. */
export const Card = forwardRef<
  HTMLDivElement,
  { children: ReactNode; className?: string; padded?: boolean }
>(function Card({ children, className = '', padded = true }, ref) {
  return (
    <div ref={ref} className={`card ${padded ? 'p-4' : ''} ${className}`}>
      {children}
    </div>
  );
});

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    /* Wraps rather than squeezes. Three buttons beside a long title on a phone
       either crush the title to two characters or push themselves off the edge; below
       `sm` they go on their own line and the title keeps its width. */
    <div className="flex flex-wrap items-start gap-x-4 gap-y-3 mb-6">
      <div className="flex-1 min-w-[12rem]">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{title}</h1>
        {subtitle ? <p className="text-sm text-muted mt-1">{subtitle}</p> : null}
      </div>
      {actions ? (
        <div className="flex items-center gap-2 flex-wrap max-sm:w-full">{actions}</div>
      ) : null}
    </div>
  );
}

export function Stat({
  label,
  value,
  tone = 'neutral',
  hint,
}: {
  label: string;
  value: string | number;
  tone?: Tone;
  hint?: string;
}) {
  const accent: Record<Tone, string> = {
    neutral: 'text-text',
    primary: 'text-primary',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  };

  return (
    <Card>
      <div className="label-text">{label}</div>
      <div className={`text-2xl font-bold mt-1 tabular ${accent[tone]}`}>{value}</div>
      {hint ? <div className="text-xs text-subtle mt-1">{hint}</div> : null}
    </Card>
  );
}

export function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message?: string;
  action?: ReactNode;
}) {
  return (
    <div className="text-center py-16 px-4">
      <p className="font-semibold">{title}</p>
      {message ? <p className="text-sm text-muted mt-1 max-w-md mx-auto">{message}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useI18n();
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-sm text-muted">
      <span
        className="w-4 h-4 rounded-full border-2 border-border border-t-primary animate-spin"
        aria-hidden
      />
      {label ?? t('common.loading')}
    </div>
  );
}

export function ErrorNotice({ error }: { error: unknown }) {
  const { t } = useI18n();
  // "Failed to fetch" is the browser's words, not ours: it says nothing a person can act
  // on. A connection problem and a database still starting get their own plain message.
  const code = (error as { code?: string } | null)?.code;
  const message =
    code === 'NETWORK'
      ? t('common.networkError')
      : code === 'DATABASE_STARTING'
        ? t('common.databaseStarting')
        : error instanceof Error
          ? error.message
          : t('common.unknownError');
  return (
    <Card className="border-[var(--color-danger)] bg-danger-soft">
      <p className="text-sm font-semibold text-danger">{t('common.somethingWrong')}</p>
      <p className="text-sm text-muted mt-1">{message}</p>
      <button type="button" className="btn-secondary mt-3" onClick={() => window.location.reload()}>
        {t('common.tryAgain')}
      </button>
    </Card>
  );
}

export function Avatar({ name, size = 32, photoUrl }: { name: string; size?: number; photoUrl?: string | null }) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className="rounded-full object-cover shrink-0 bg-surface-alt"
        style={{ width: size, height: size }}
      />
    );
  }
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const text = ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase();

  return (
    <span
      aria-label={name}
      className="inline-flex items-center justify-center rounded-full bg-[var(--color-primary-soft)] text-primary font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {text}
    </span>
  );
}

/**
 * A labelled form field.
 *
 * `group` for anything that is not a single input — a map, a list of people, a list of
 * files. A <label> forwards every click inside it to the first button or input it
 * contains: around the site map that was the map's own "+" zoom button, so every click
 * on the map zoomed in; around the attachments list, clicking a file's name pressed the
 * first "Remove". A group is a plain container that only names its contents.
 */
export function Field({
  label,
  children,
  hint,
  group = false,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  group?: boolean;
}) {
  const inner = (
    <>
      <span className="label-text block mb-1.5">{label}</span>
      {children}
      {hint ? <span className="block text-xs text-subtle mt-1">{hint}</span> : null}
    </>
  );
  return group ? (
    <div className="block" role="group" aria-label={label}>
      {inner}
    </div>
  ) : (
    <label className="block">{inner}</label>
  );
}
